import { useCallback, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { newConversationHref } from '@/frontend/layouts/worker-first/agentAccess'
import { isEditableShortcutTarget } from '@/frontend/lib/keyboardShortcuts'

/**
 * "Nova conversa" (Iteração 3, Ajuste "página de início"): a ação primária do
 * shell + o atalho ⌘N/Ctrl+N levam à PÁGINA DEDICADA de início de conversa
 * (`/dashboard/agent_chats/new`) — saudação + seletor de agente + composer —
 * em vez de cair direto na aba Chat de um agente. É essa página que resolve o
 * agente-alvo (último usado → 1º pronto), o gate SZ-1583 (só agentes prontos
 * são selecionáveis) e os estados (0 pronto → convida a contratar/aguardar).
 * Ao enviar de lá, a página cria a conversa e navega pra aba Chat do agente
 * com a mensagem já enviada (auto-envio no mount). O "Conversar" da home
 * também aponta pra cá (consistência).
 *
 * O ⌘N/Ctrl+N segue o padrão do ⌘K da CommandPalette (keydown no document +
 * preventDefault) e vale em todas as páginas do dashboard. Trade-off mantido
 * da Iteração 1, decidido e documentado: com a aba focada, o ⌘N nativo do
 * navegador (nova janela) é suprimido onde o evento chega à página (macOS
 * Chromium/Firefox) — escolha chat-first intencional, espelhando o atalho do
 * ChatGPT/Codex. Onde o navegador reserva o combo (Ctrl+N no Windows/Linux
 * Chrome), a página nunca recebe o keydown e o nativo vence.
 */
export function useNewConversation(onNavigate: () => void) {
  const startNewConversation = useCallback(() => {
    onNavigate()
    router.visit(newConversationHref())
  }, [onNavigate])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return
      if (e.key.toLowerCase() !== 'n') return
      if (isEditableShortcutTarget(e.target)) return
      e.preventDefault()
      startNewConversation()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [startNewConversation])

  return { startNewConversation }
}
